import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArtWork } from "../types/ArtWork";
import { DigitalArtwork } from "../data/digitalArtworks";
import { useDigitalArtwork } from "../hooks/useTranslatedArtwork";
import { isRtlLanguage } from "../i18n-config";
import { artworkPagePath } from "../lib/artworkPaths";
import { ArtworkImage } from "./ArtworkImage";
import { ArtworkLightboxActions } from "./ArtworkLightboxActions";
import { ArtworkExternalLinks } from "./ArtworkExternalLinks";

export function DigitalArtworkRow({
  artWork,
  index,
}: {
  artWork: ArtWork;
  index: number;
}) {
  const { t, i18n } = useTranslation();
  const translated = useDigitalArtwork(artWork);
  const isRtl = isRtlLanguage(i18n.language);
  const imageFirst = isRtl ? index % 2 !== 0 : index % 2 === 0;

  return (
    <article
      className={`flex flex-col gap-8 lg:items-center ${
        imageFirst ? "lg:flex-row" : "lg:flex-row-reverse"
      }`}
    >
      <div className="lg:w-1/2">
        <div className="art-card overflow-hidden">
          <ArtworkImage
            src={translated.imageUrl}
            alt={translated.name}
            aspectClass="aspect-[4/3]"
          />
        </div>
      </div>
      <div className="flex flex-col justify-center lg:w-1/2 lg:px-8">
        <h3 className="font-display text-3xl font-semibold text-canvas-950 sm:text-4xl">
          {translated.name}
        </h3>
        <p className="mt-4 line-clamp-4 leading-relaxed text-canvas-600">
          {translated.description}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to={artworkPagePath("digital", artWork.id)}
            className="btn-primary !px-5 !py-2.5 !text-xs"
          >
            {t("home.digital.viewArtwork")}
          </Link>
        </div>
      </div>
    </article>
  );
}

export function DigitalGalleryCard({
  work,
  onClick,
  to,
  className = "",
}: {
  work: DigitalArtwork;
  onClick?: () => void;
  to?: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const translated = useDigitalArtwork(work);

  const imageBlock = (
    <ArtworkImage
      src={translated.imageUrl}
      alt={translated.name}
      aspectClass="aspect-[4/3]"
      imgClassName="transition-transform duration-700 group-hover:scale-105"
    />
  );

  const textBlock = (
    <>
      <h3 className="font-display text-xl font-medium text-canvas-950">
        {translated.name}
      </h3>
      <p className="mt-2 line-clamp-3 flex-1 text-sm text-canvas-600">
        {translated.description}
      </p>
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        className={`group art-card flex flex-col animate-fade-in ${className}`}
      >
        {imageBlock}
        <div className="flex flex-1 flex-col p-5">{textBlock}</div>
      </Link>
    );
  }

  return (
    <article className={`group art-card flex flex-col animate-fade-in ${className}`}>
      <button
        type="button"
        onClick={onClick}
        className="w-full border-0 p-0 text-start"
      >
        {imageBlock}
      </button>
      <div className="flex flex-1 flex-col p-5">
        <button
          type="button"
          onClick={onClick}
          className="border-0 bg-transparent p-0 text-start"
        >
          {textBlock}
        </button>
        <button
          type="button"
          onClick={onClick}
          className="btn-primary mt-4 w-full border-0 !px-4 !py-2 !text-xs"
        >
          {t("gallery.viewDetails")}
        </button>
      </div>
    </article>
  );
}

export function FeaturedDigitalCard({
  work,
  onClick,
}: {
  work: ArtWork;
  onClick: () => void;
}) {
  const translated = useDigitalArtwork(work);
  return (
    <button
      type="button"
      onClick={onClick}
      className="group art-card w-full overflow-hidden border-0 p-0 text-start"
    >
      <ArtworkImage
        src={translated.imageUrl}
        alt={translated.name}
        aspectClass="aspect-[4/3]"
        imgClassName="transition-transform duration-500 group-hover:scale-105"
      />
      <div className="p-3 sm:p-4">
        <h3 className="font-display text-base font-medium text-canvas-950 line-clamp-2 sm:text-lg">
          {translated.name}
        </h3>
      </div>
    </button>
  );
}

export function DigitalLightboxContent({
  work,
  exploreGalleryHref,
}: {
  work: ArtWork;
  exploreGalleryHref?: string;
}) {
  const translated = useDigitalArtwork(work);
  return (
    <>
      <p className="text-xs font-medium uppercase tracking-[0.15em] text-accent">
        {translated.collectionLabel}
      </p>
      <h2 className="mt-2 font-display text-xl font-semibold text-canvas-950 sm:text-2xl md:text-3xl">
        {translated.title}
      </h2>
      {translated.description && (
        <p className="mt-3 text-sm leading-relaxed text-canvas-600 sm:mt-4">
          {translated.description}
        </p>
      )}
      <ArtworkExternalLinks links={work.links} compact className="mt-4 sm:mt-5" />
      <ArtworkLightboxActions
        artworkPageHref={artworkPagePath("digital", work.id)}
        exploreGalleryHref={exploreGalleryHref}
        share={{
          medium: "digital",
          id: work.id,
          title: translated.title,
          text: translated.description,
        }}
        inquire={{
          medium: "digital",
          id: work.id,
          name: translated.name,
        }}
      />
    </>
  );
}
